import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../../../../utils/api";
import { motion } from "framer-motion";
import Loader from "../../../../ui/Loader";
import Table from "../../../../ui/Table";
import Navbar from "../../../../ui/Navbar";

export default function ScoreHistory() {
  // Extract answerPaperId from URL path
  const pathSegments = window.location.pathname.split("/");
  const answerPaperId = pathSegments[pathSegments.length - 1];

  // Initialize as an empty array instead of null
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tab state to switch between aggregated table and normal view
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("activeTab") || "table"
  );

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const fetchHistoryResults = async () => {
      if (!answerPaperId) {
        setError("Invalid URL: No answer paper ID found");
        setLoading(false);
        return;
      }
      try {
        const serverUrl = process.env.REACT_APP_SERVER_URL;
        const response = await fetchWithAuth(
          `${serverUrl}/api/score/list/${answerPaperId}`,
          {
            method: 'GET',
            credentials: 'include'
          }
        );
        if (!response) return;
        const data = await response.json();
        // Update response structure to match axios format
        response.data = data;
        // Ensure that historyData is always an array
        setHistoryData(response.data.score || []);
      } catch (err) {
        setError("Failed to fetch score history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistoryResults();
  }, [answerPaperId]);

  if (loading) {
    return (
      <div className="results-page-wrapper">
        <Navbar />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          <Loader style={{ fontSize: "50px", position: "static", left: "auto", top: "auto" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page-wrapper">
        <Navbar />
        <div
          className="error"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (historyData.length === 0) {
    return (
      <div className="results-page-wrapper">
        <Navbar />
        <div
          className="no-data"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          No history available.
        </div>
      </div>
    );
  }

  const questionSet = new Set();
  historyData.forEach((entry) =>
    entry.result.forEach((q) => questionSet.add(q.questionNumber))
  );
  const questions = Array.from(questionSet).sort();

  // Build aggregated table rows with userPrompt for each attempt.
  const aggregatedTableData = questions.map((qNum) => {
    const row = { question: qNum };
    historyData.forEach((entry, idx) => {
      const question = entry.result.find((q) => q.questionNumber === qNum);
      row[`attempt${idx + 1}`] = question ? question.obtainedScore : "-";
      row[`feedbackAttempt${idx + 1}`] = question ? question.feedback : "-";
      row[`userPromptAttempt${idx + 1}`] = question ? question.userPrompt : "-";
    });
    const latestEntry = historyData[historyData.length - 1];
    const latestQuestion = latestEntry.result.find(
      (q) => q.questionNumber === qNum
    );
    if (latestQuestion) {
      row["questionText"] = latestQuestion.questionText;
      row["studentAnswer"] = latestQuestion.studentAnswer;
      row["maxScore"] = latestQuestion.maxScore;
      row["feedbackLatest"] = latestQuestion.feedback;
    } else {
      row["questionText"] = "-";
      row["studentAnswer"] = "-";
      row["maxScore"] = "-";
      row["feedbackLatest"] = "-";
    }
    return row;
  });

  // Build columns for the aggregated view including userPrompt columns.
  const aggregatedColumns = [
    { key: "question", label: "Question Number", sortable: false },
    { key: "questionText", label: "Question Text", sortable: false },
    { key: "studentAnswer", label: "Student's Answer", sortable: false },
  ];
  historyData.forEach((_, idx) => {
    aggregatedColumns.push({
      key: `attempt${idx + 1}`,
      label: `Obtained Score ${idx + 1}`,
      sortable: false,
    });
    aggregatedColumns.push({
      key: `feedbackAttempt${idx + 1}`,
      label: `Feedback ${idx + 1}`,
      sortable: false,
    });
    aggregatedColumns.push({
      key: `userPromptAttempt${idx + 1}`,
      label: `User Prompt ${idx + 1}`,
      sortable: false,
    });
  });
  aggregatedColumns.push(
    { key: "maxScore", label: "Max Score", sortable: false },
    { key: "feedbackLatest", label: "Feedback (Latest)", sortable: false }
  );

  // For Normal view: display each history entry separately.
  // Including User Prompt column in each result row.
  const normalColumns = [
    { key: "questionNumber", label: "Question Number", sortable: false },
    { key: "questionText", label: "Question Text", sortable: false },
    { key: "studentAnswer", label: "Student's Answer", sortable: false },
    { key: "userPrompt", label: "User Prompt", sortable: false },
    { key: "obtainedScore", label: "Obtained Score", sortable: false },
    { key: "maxScore", label: "Max Score", sortable: false },
    { key: "feedback", label: "Feedback", sortable: false },
  ];

  // Function to export aggregated table data to CSV
  const downloadCSV = () => {
    // Extract column headers from aggregatedColumns
    const header = aggregatedColumns.map((col) => col.label);
    let csvContent = header.join(",") + "\r\n";
    aggregatedTableData.forEach((row) => {
      const rowArray = aggregatedColumns.map((col) => {
        // Map column key to row data; add quotes if string contains commas or quotes
        let cell = row[col.key] !== undefined ? row[col.key] : "";
        cell = cell.toString();
        if (cell.includes(",") || cell.includes('"')) {
          cell = '"' + cell.replace(/"/g, '""') + '"';
        }
        return cell;
      });
      csvContent += rowArray.join(",") + "\r\n";
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${answerPaperId}_score_history.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Navbar />
      <div className="results-page">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="page-title"
        >
          Score History Comparison for Answer Paper ID: {answerPaperId}
        </motion.h1>

        {/* Export to CSV Button */}
        <motion.button
          className="csv-download-button"
          onClick={downloadCSV}
          whileHover={{ scale: 1.05 }}
        >
          <i className="fa-solid fa-download"></i> Export to CSV
        </motion.button>

        {/* Tab Bar to switch between aggregated view and normal view */}
        <div className="tab-bar">
          <button
            className={`tab-button ${activeTab === "table" ? "active" : ""}`}
            onClick={() => setActiveTab("table")}
            style={{
              backgroundColor: activeTab === "table" ? "#4a90e2" : "#f5f5f5",
              color: activeTab === "table" ? "white" : "#333",
              transform: activeTab === "table" ? "scale(1.05)" : "scale(1)",
              transition: "all 0.3s ease",
            }}
          >
            <i className="fa-solid fa-table"></i>
          </button>
          <button
            className={`tab-button ${activeTab === "normal" ? "active" : ""}`}
            onClick={() => setActiveTab("normal")}
            style={{
              backgroundColor: activeTab === "normal" ? "#4a90e2" : "#f5f5f5",
              color: activeTab === "normal" ? "white" : "#333",
              transform: activeTab === "normal" ? "scale(1.05)" : "scale(1)",
              transition: "all 0.3s ease",
            }}
          >
            <i className="fa-solid fa-list"></i>
          </button>
        </div>

        {activeTab === "table" && (
          <Table
            data={aggregatedTableData}
            columns={aggregatedColumns}
            caption="Comparison of Obtained Scores, Feedback, and User Prompts"
            textAlign="left"
          />
        )}

        {activeTab === "normal" && (
          <>
            {historyData.map((scoreItem, index) => (
              <div key={scoreItem._id} className="score-history-item">
                <Table
                  data={scoreItem.result}
                  columns={normalColumns}
                  caption={`Score History ${index + 1}`}
                  textAlign="left"
                />
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}
